import type { PropsWithChildren } from 'react';

import { DEALER_FROZEN_STATUSES } from 'Utilities/consts';
import { useHandleKycStatus } from 'Utilities/hooks/useHandleKycStatus';
import { useIsKycFrozen } from 'Utilities/hooks/useIsKycFrozen/useIsKycFrozen';
import useUser from 'Utilities/hooks/useUser';

import type { BiddingSectionCTAStateControllerProps } from './BiddingSectionCTAStateController.types';
import {
	BiddingFrozen,
	BiddingKycFrozen,
	OtherContactHasBidOrOffered,
	PlaceBidKycAfterSubmit,
	PlaceBidKycBeforeSubmit,
	UserHasBidOrOffered,
} from './CTAStates';

export const BiddingSectionCTAStateController: React.FC<PropsWithChildren<BiddingSectionCTAStateControllerProps>> = ({
	children,
	hasOtherContactActiveBid,
	latestUserBidOrOffer,
	state,
}) => {
	const { dealerStatus } = useUser();

	// Boolean() cannot be used to perform a null check - https://github.com/microsoft/TypeScript/issues/16655
	const hasUserBidOrOffered = latestUserBidOrOffer !== null;

	const { isKycCompleted, isKycInProgress } = useHandleKycStatus({
		source: 'BiddingSectionCTAStateController',
	});

	const { isKycFrozen } = useIsKycFrozen();

	// Replace with as const - DEP-56
	const isFrozen = DEALER_FROZEN_STATUSES.has(dealerStatus?.slug ?? '');

	switch (true) {
		case isKycFrozen:
			return <BiddingKycFrozen />;
		case isKycInProgress():
			return <PlaceBidKycBeforeSubmit />;
		case isKycCompleted():
			return <PlaceBidKycAfterSubmit />;
		case isFrozen:
			return <BiddingFrozen state={state} />;
		case hasOtherContactActiveBid:
			return <OtherContactHasBidOrOffered state={state} />;
		case hasUserBidOrOffered:
			// latestUserBidOrOffer is not null here and this issue is fixed in latest TS version
			return (
				<UserHasBidOrOffered latestUserBidOrOffer={latestUserBidOrOffer!} state={state}>
					{children}
				</UserHasBidOrOffered>
			);
		default:
			return <>{children}</>;
	}
};
